import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Roles, routes } from '../../constants/app-constants';
import { RouteList } from '../common.model';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private subject = new Subject<boolean>();
  private routeAccessList: { [key: string]: boolean } = {};
  private routeList = routes;
  private isEmbeddedView = new BehaviorSubject<boolean>(false);
  isEmbeddedViewasObservable = this.isEmbeddedView.asObservable();

  // ── Tenant logo ──────────────────────────────────────────────────────────
  // Default is the local JIO SVG asset. After a successful fetch the value
  // is replaced with a blob URL so the custom logo shows in the sidebar.
  private static readonly DEFAULT_LOGO = 'assets/images/sidenav/jio.svg';
  private readonly logoSrc = new BehaviorSubject<string>(
    AppService.DEFAULT_LOGO
  );
  readonly tenantLogoUrl$ = this.logoSrc.asObservable();

  /**
   * Track the last blob URL so we can revoke it before replacing — prevents
   * a memory leak when loadTenantLogo is called multiple times (e.g. after
   * a logo upload via the Customize page).
   */
  private currentLogoBlobUrl: string | null = null;

  /**
   * Fetch the tenant logo using the same fetch() + X-Disable-Cache header
   * that the Customize component uses — this is what the server authenticates.
   * On success the blob URL is emitted. On failure the JIO SVG stays active.
   */
  loadTenantLogo(): void {
    if (this.currentLogoBlobUrl) return;
    const url = `${
      environment.customImagesUrl
    }/custom-images/logo.png?v=${Date.now()}`;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    fetch(url, { headers: { 'X-Disable-Cache': 'true' } })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Logo fetch failed');
        }
        return response.blob();
      })
      .then((blob) => {
        // Revoke the previous blob URL to free memory before creating a new one.
        if (this.currentLogoBlobUrl) {
          URL.revokeObjectURL(this.currentLogoBlobUrl);
        }
        const blobUrl = URL.createObjectURL(blob);
        this.currentLogoBlobUrl = blobUrl;
        this.logoSrc.next(blobUrl);
        return undefined;
      })
      .catch(() => {
        this.currentLogoBlobUrl = null;
        this.logoSrc.next(AppService.DEFAULT_LOGO);
      });
  }

  /** Called by Customize after a successful logo upload. */
  refreshTenantLogo(): void {
    if (this.currentLogoBlobUrl) {
      URL.revokeObjectURL(this.currentLogoBlobUrl);
      this.currentLogoBlobUrl = null;
    }
    this.loadTenantLogo();
  }

  constructor(
    private keycloakService: KeycloakService,
    private httpClient: HttpClient
  ) {}

  sendPreviewPage(isP: boolean) {
    this.subject.next(isP);
  }

  setIsEmbeddedView(val: boolean) {
    this.isEmbeddedView.next(val);
  }

  isPreviewPage(): Observable<boolean> {
    return this.subject.asObservable();
  }

  checkRoles() {
    const userRoles = this.keycloakService.getUserRoles();

    const parentRouteList = this.routeList.map((route) => ({
      roles: route.roles,
      shouldHaveAllRoles: route.shouldHaveAllRoles,
      link: route.link
    }));

    const childrenRouteList = this.routeList.reduce(
      (
        accumulator: {
          roles: string[];
          shouldHaveAllRoles: boolean;
          link: string;
        }[],
        currentValue
      ) => {
        const nestedArrayObjects =
          currentValue.subMenu?.map((role) => ({
            roles: role.roles,
            shouldHaveAllRoles: role.shouldHaveAllRoles,
            link: role.link
          })) || [];
        return accumulator.concat(nestedArrayObjects);
      },
      []
    );

    const finalRouteList = [...parentRouteList, ...childrenRouteList];

    finalRouteList.map((route) => {
      if (route.shouldHaveAllRoles) {
        if (route.roles.every((role) => userRoles.includes(role))) {
          this.routeAccessList[route.link] = true;
        }
      } else {
        if (route.roles.some((role) => userRoles.includes(role))) {
          this.routeAccessList[route.link] = true;
        }
      }
    });
  }

  hasAccess(route: string) {
    return this.routeAccessList[route] || false;
  }

  getRouteAccessList() {
    return this.routeAccessList;
  }

  getSideBarNav(): RouteList[] {
    const routeList = this.routeList
      .filter((route) => this.hasAccess(route.link))
      .map((r) => {
        return {
          ...r,
          ...(r.subMenu && {
            subMenu: r.subMenu.filter((f) => this.hasAccess(String(f.link)))
          })
        };
      });
    return routeList;
  }

  doesUserHaveRole(role: string[]) {
    return this.keycloakService
      .getUserRoles()
      .some((aRole) => [Roles.JIOX_ADMIN, ...role].includes(aRole));
  }

  getUserKeycloakToken(jwtToken: string): Observable<{
    token: string;
    resources: { [key: string]: { roles: string[] } };
  }> {
    return this.httpClient.post<{
      token: string;
      resources: { [key: string]: { roles: string[] } };
    }>(environment.apiHost + '/embedded-app/get-auth-token', { jwtToken });
  }
}
